// src/components/BotConnection/CaptchaModal.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CaptchaModal = ({ isOpen, captchaData, onInteractTile, onSkip, onClose }) => {
    const [rotations, setRotations] = useState({});

    useEffect(() => {
        if (isOpen) {
            setRotations({});
        }
    }, [isOpen, captchaData]);

    if (!isOpen || !captchaData) return null;

    const frames = captchaData.frames || [];
    const botName = captchaData.botName || 'Бот';

    const handleTileClick = (entityId, index) => {
        setRotations(prev => ({
            ...prev,
            [index]: ((prev[index] || 0) + 90) % 360
        }));
        if (onInteractTile) {
            onInteractTile(entityId, index);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="w-full max-w-lg overflow-hidden rounded-2xl bg-gray-900/90 border border-purple-500/30 shadow-2xl shadow-purple-900/40"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-800 bg-gray-950/60">
                        <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
                            <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-purple-600/20 text-purple-400 border border-purple-500/40 shrink-0">
                                <i className="fas font-bold fa-puzzle-piece text-lg sm:text-xl"></i>
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-sm sm:text-lg font-bold text-white tracking-wide truncate">
                                    Прохождение Капчи <span className="text-purple-400">({botName})</span>
                                </h3>
                                <p className="text-[11px] sm:text-xs text-gray-400 truncate">
                                    Кликайте по рамкам для разворота
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Content 3x3 Seamless Grid */}
                    <div className="p-3 sm:p-6 flex justify-center">
                        <div className="w-full max-w-[270px] sm:max-w-[360px] aspect-square grid grid-cols-3 gap-0 overflow-hidden rounded-xl border border-purple-500/30 shadow-2xl shadow-purple-950/60 bg-black">
                            {frames.map((tile, idx) => {
                                const currentRotation = rotations[idx] ?? (tile.rotation || 0);
                                return (
                                    <button
                                        key={tile.entityId || idx}
                                        onClick={() => handleTileClick(tile.entityId, idx)}
                                        className="relative group aspect-square overflow-hidden bg-gray-950 transition-all focus:outline-none hover:z-20 hover:ring-2 hover:ring-inset hover:ring-purple-400 hover:brightness-110"
                                    >
                                        <img
                                            src={tile.imageBase64 || tile.image}
                                            alt={`Frame ${idx + 1}`}
                                            className="w-full h-full object-cover transition-transform duration-200 ease-out select-none pointer-events-none"
                                            style={{ transform: `rotate(${currentRotation}deg)` }}
                                        />
                                        <div className="absolute inset-0 bg-purple-600/10 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity pointer-events-none">
                                            <div className="w-7 h-7 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center text-purple-300 shadow-md">
                                                <i className="fas fa-redo-alt text-xs"></i>
                                            </div>
                                        </div>
                                        <span className="absolute bottom-1 right-1 text-[9px] font-mono px-1 py-0.2 rounded bg-black/70 text-gray-400 opacity-60 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            #{idx + 1}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2 px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-800 bg-gray-950/50">
                        <button
                            onClick={onSkip}
                            className="px-4 py-2.5 text-xs font-semibold text-gray-400 hover:text-red-400 transition-colors text-center min-h-[40px] flex items-center justify-center"
                        >
                            Пропустить этого бота
                        </button>
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 text-xs sm:text-sm font-semibold rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-600/30 transition-all text-center min-h-[40px] flex items-center justify-center cursor-pointer"
                        >
                            Готово
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default CaptchaModal;
